import { EntityCommentThread } from '@agentic-flow-studio/shared/components/comments/EntityCommentThread';
import { JobAttachmentsSection } from '@agentic-flow-studio/shared/components/job-plan/JobAttachmentsSection';
import { JobLocationMap } from '@agentic-flow-studio/shared/components/job-plan/JobLocationMap';
import { JobSlotFinderDialog } from '@agentic-flow-studio/shared/components/job-plan/JobSlotFinderDialog';
import { formatOffsetTime } from '@agentic-flow-studio/shared/lib/job-plan-time';
import { isTerminalStatus } from '@agentic-flow-studio/shared/lib/task-list';
import { trpc } from '@agentic-flow-studio/shared/lib/trpc';
import type { JobStatus } from '@agentic-flow-studio/shared/types/job-plan';
import { Badge } from '@agentic-flow-studio/shared/ui/badge';
import { Button } from '@agentic-flow-studio/shared/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@agentic-flow-studio/shared/ui/card';
import { Input } from '@agentic-flow-studio/shared/ui/input';
import {
  AlertCircle,
  ArrowLeft,
  Calendar,
  CalendarDays,
  Camera,
  CheckCircle2,
  Clock,
  Edit2,
  ExternalLink,
  Layers,
  ListTodo,
  Loader2,
  Mail,
  MapPin,
  MessageSquare,
  Phone,
  Plus,
  RotateCcw,
  Sparkles,
  Trash2,
  Truck,
  User,
  UserCheck,
  UserX,
  Wrench,
} from 'lucide-react';
import type React from 'react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

export default function JobDetailPage() {
  const { orgId, workspaceId, jobId } = useParams<{
    orgId: string;
    workspaceId: string;
    jobId: string;
  }>();
  const navigate = useNavigate();
  const utils = trpc.useUtils();

  const basePath = `/org/${orgId}/workspace/${workspaceId}/jobs`;
  const numJobId = jobId ? parseInt(jobId, 10) : 0;

  const [newTaskLabel, setNewTaskLabel] = useState('');
  const [selectedOpToAssign, setSelectedOpToAssign] = useState<string>('');
  const [isRescheduling, setIsRescheduling] = useState(false);
  const [newDateValue, setNewDateValue] = useState('');
  const [isAdjustingPin, setIsAdjustingPin] = useState(false);
  const [isSlotFinderOpen, setIsSlotFinderOpen] = useState(false);
  const [pinLatitude, setPinLatitude] = useState<number | null>(null);
  const [pinLongitude, setPinLongitude] = useState<number | null>(null);

  const {
    data: job,
    isLoading,
    error,
  } = trpc.jobPlan.jobs.getById.useQuery({ id: numJobId }, { enabled: numJobId > 0 });

  const { data: operators = [] } = trpc.jobPlan.operators.list.useQuery(
    { activeOnly: true },
    { enabled: numJobId > 0 }
  );

  const { data: jobTasks = [] } = trpc.tasks.getAll.useQuery(undefined, {
    enabled: numJobId > 0,
  });

  const filteredTasks = jobTasks.filter((t) => t.jobId === numJobId);
  // Checklist completion follows the workspace's configured terminal status. This compared
  // against the literal 'completed' — a key no other surface used — so the count never moved.
  const taskStatusesQuery = trpc.taskStatuses.list.useQuery();
  const taskStatuses = taskStatusesQuery.data?.items ?? [];

  const transitionMutation = trpc.jobPlan.jobs.transitionStatus.useMutation({
    onSuccess: () => {
      toast.success('Job status updated');
      utils.jobPlan.jobs.getById.invalidate({ id: numJobId });
      utils.jobPlan.jobs.list.invalidate();
      utils.contacts.getActivityFeed.invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const assignJobMutation = trpc.jobPlan.assignJob.useMutation({
    onSuccess: () => {
      toast.success('Job assigned to operator');
      setSelectedOpToAssign('');
      utils.jobPlan.jobs.getById.invalidate({ id: numJobId });
      utils.jobPlan.jobs.list.invalidate();
      utils.contacts.getActivityFeed.invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const rescheduleMutation = trpc.jobPlan.jobs.reschedule.useMutation({
    onSuccess: () => {
      toast.success('Job rescheduled successfully');
      setIsRescheduling(false);
      utils.jobPlan.jobs.getById.invalidate({ id: numJobId });
      utils.jobPlan.jobs.list.invalidate();
      utils.contacts.getActivityFeed.invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const updatePinMutation = trpc.jobPlan.jobs.update.useMutation({
    onSuccess: () => {
      toast.success('Job location updated');
      setIsAdjustingPin(false);
      utils.jobPlan.jobs.getById.invalidate({ id: numJobId });
      utils.jobPlan.jobs.list.invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const reattemptMutation = trpc.jobPlan.jobs.reattempt.useMutation({
    onSuccess: () => {
      toast.success('Job returned to unassigned pool for re-attempt');
      utils.jobPlan.jobs.getById.invalidate({ id: numJobId });
      utils.jobPlan.jobs.list.invalidate();
      utils.contacts.getActivityFeed.invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const createTaskMutation = trpc.tasks.create.useMutation({
    onSuccess: () => {
      setNewTaskLabel('');
      utils.tasks.getAll.invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const deleteJobMutation = trpc.jobPlan.jobs.delete.useMutation({
    onSuccess: () => {
      toast.success('Job deleted');
      utils.jobPlan.jobs.list.invalidate();
      navigate(basePath);
    },
    onError: (err) => toast.error(err.message),
  });

  const handleAddTask = (e: React.FormEvent) => {
    e.preventDefault();
    if (!numJobId || !newTaskLabel.trim()) return;
    createTaskMutation.mutate({
      label: newTaskLabel.trim(),
      jobId: numJobId,
    });
  };

  const handleStatusTransition = (targetStatus: JobStatus) => {
    if (!numJobId) return;
    transitionMutation.mutate({
      id: numJobId,
      targetStatus,
    });
  };

  const handleAssignOperator = () => {
    if (!numJobId || !selectedOpToAssign || !job) return;
    assignJobMutation.mutate({
      jobId: numJobId,
      operatorProfileId: parseInt(selectedOpToAssign, 10),
      serviceDate: job.serviceDate,
    });
  };

  const handleReschedule = () => {
    if (!numJobId || !newDateValue) return;
    rescheduleMutation.mutate({
      jobId: numJobId,
      newServiceDate: newDateValue,
    });
  };

  const handleSavePin = () => {
    if (!numJobId || pinLatitude == null || pinLongitude == null) return;
    updatePinMutation.mutate({
      id: numJobId,
      latitude: pinLatitude,
      longitude: pinLongitude,
      // The pin was placed by hand, not resolved through Places, so its old place ID no
      // longer describes this exact point -- drop it rather than leave it misleadingly stale.
      googlePlaceId: null,
    });
  };

  const handleReattempt = () => {
    if (!numJobId) return;
    reattemptMutation.mutate({
      jobId: numJobId,
    });
  };

  const handleDelete = () => {
    if (!numJobId) return;
    if (confirm('Are you sure you want to delete this job?')) {
      deleteJobMutation.mutate({ id: numJobId });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-muted-foreground gap-2">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span>Loading job details...</span>
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-6 text-center space-y-3">
        <p className="text-sm font-semibold text-foreground">Job not found</p>
        <p className="text-xs text-muted-foreground">
          This job may have been deleted or moved to a different workspace.
        </p>
        <Button size="sm" asChild variant="outline">
          <Link to={basePath}>
            <ArrowLeft className="h-4 w-4 mr-1.5" />
            Back to Jobs
          </Link>
        </Button>
      </div>
    );
  }

  const statusColors: Record<string, string> = {
    unassigned: 'text-amber-600 bg-amber-500/10 border-amber-500/20',
    assigned: 'text-blue-600 bg-blue-500/10 border-blue-500/20',
    in_progress: 'text-purple-600 bg-purple-500/10 border-purple-500/20',
    completed: 'text-emerald-600 bg-emerald-500/10 border-emerald-500/20',
    failed: 'text-rose-600 bg-rose-500/10 border-rose-500/20',
    canceled: 'text-muted-foreground bg-muted border-border',
  };

  return (
    <div className="flex flex-col h-full overflow-hidden bg-background">
      {/* Top Sticky Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-border/40 bg-card">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            asChild
            className="h-8 gap-1.5 text-muted-foreground hover:text-foreground"
          >
            <Link to={basePath}>
              <ArrowLeft className="h-4 w-4" />
              <span>Jobs</span>
            </Link>
          </Button>

          <span className="text-muted-foreground/50">/</span>

          <h1 className="text-lg font-bold tracking-tight text-foreground truncate max-w-md">
            {job.title}
          </h1>

          <Badge
            variant="outline"
            className={`capitalize text-xs font-semibold ${
              statusColors[job.status] || 'text-muted-foreground'
            }`}
          >
            {job.status.replace('_', ' ')}
          </Badge>
        </div>

        <div className="flex items-center gap-2.5">
          <Button
            variant="outline"
            size="sm"
            className="text-destructive hover:bg-destructive/10 border-destructive/30 gap-1.5"
            onClick={handleDelete}
            disabled={deleteJobMutation.isPending}
          >
            {deleteJobMutation.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Trash2 className="h-3.5 w-3.5" />
            )}
            <span>Delete</span>
          </Button>

          <Button size="sm" asChild className="gap-1.5">
            <Link to={`${basePath}/${job.id}/edit`}>
              <Edit2 className="h-3.5 w-3.5" />
              <span>Edit Job</span>
            </Link>
          </Button>
        </div>
      </div>

      {/* Main Full Page Scrollable Content */}
      <div className="flex-1 overflow-y-auto px-6 py-6 max-w-6xl w-full mx-auto grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column (2 Cols): Metadata, Timing, Skills, Checklist */}
        <div className="lg:col-span-2 space-y-6">
          {/* Re-attempt / Failed Notice Banner if failed or canceled */}
          {(job.status === 'failed' || job.status === 'canceled') && (
            <div className="p-4 rounded-xl border border-rose-500/30 bg-rose-500/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
              <div className="flex items-start gap-2.5">
                <AlertCircle className="h-5 w-5 text-rose-600 dark:text-rose-400 shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-xs font-bold text-rose-700 dark:text-rose-300">
                    Job Was Marked {job.status.toUpperCase()}
                  </h4>
                  <p className="text-xs text-rose-600/90 dark:text-rose-400/90 mt-0.5">
                    {job.resolutionNote || 'No resolution note provided.'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 text-xs bg-background gap-1.5"
                  onClick={handleReattempt}
                  disabled={reattemptMutation.isPending}
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                  <span>Re-attempt (Send to Pool)</span>
                </Button>
              </div>
            </div>
          )}

          {/* Overview Card */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center justify-between">
                <span>Job Overview</span>
                {job.priority > 0 && (
                  <Badge variant="secondary" className="text-xs bg-amber-500/15 text-amber-600">
                    Priority {job.priority}
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start gap-2.5 p-3 rounded-lg border border-border/50 bg-muted/20">
                <MapPin className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                <div className="flex flex-col">
                  <span className="text-xs font-semibold text-foreground">Service Address</span>
                  <span className="text-xs text-muted-foreground mt-0.5">
                    {job.formattedAddress}
                  </span>
                  {job.locationNote && (
                    <span className="text-[11px] text-muted-foreground/80 mt-1 italic">
                      Note: {job.locationNote}
                    </span>
                  )}
                </div>
              </div>

              {/* Map Location & Pin Override */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-foreground">Map Location</span>
                  {!isAdjustingPin ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 text-[11px] text-primary px-2"
                      onClick={() => {
                        setPinLatitude(job.latitude ?? null);
                        setPinLongitude(job.longitude ?? null);
                        setIsAdjustingPin(true);
                      }}
                    >
                      <MapPin className="h-3 w-3 mr-1" />
                      <span>Adjust Pin</span>
                    </Button>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 text-[11px] text-muted-foreground px-2"
                      onClick={() => setIsAdjustingPin(false)}
                    >
                      Cancel
                    </Button>
                  )}
                </div>

                <JobLocationMap
                  latitude={isAdjustingPin ? pinLatitude : (job.latitude ?? null)}
                  longitude={isAdjustingPin ? pinLongitude : (job.longitude ?? null)}
                  onPinChange={
                    isAdjustingPin
                      ? (lat, lng) => {
                          setPinLatitude(lat);
                          setPinLongitude(lng);
                        }
                      : undefined
                  }
                />

                {isAdjustingPin && (
                  <div className="flex justify-end pt-1">
                    <Button
                      size="sm"
                      className="h-8 text-xs"
                      onClick={handleSavePin}
                      disabled={
                        pinLatitude == null || pinLongitude == null || updatePinMutation.isPending
                      }
                    >
                      {updatePinMutation.isPending ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                      ) : null}
                      <span>Save Pin</span>
                    </Button>
                  </div>
                )}
              </div>

              {job.description && (
                <div className="space-y-1">
                  <span className="text-xs font-semibold text-foreground">Description</span>
                  <p className="text-xs text-muted-foreground leading-relaxed whitespace-pre-wrap">
                    {job.description}
                  </p>
                </div>
              )}

              {/* Service Timing & Arrival Window */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                <div className="flex items-center gap-2.5 p-3 rounded-lg border border-border/40 bg-card">
                  <Calendar className="h-4 w-4 text-primary shrink-0" />
                  <div className="flex flex-col">
                    <span className="text-[11px] text-muted-foreground">Service Date</span>
                    <span className="text-xs font-medium text-foreground">{job.serviceDate}</span>
                  </div>
                </div>

                <div className="flex items-center gap-2.5 p-3 rounded-lg border border-border/40 bg-card">
                  <Clock className="h-4 w-4 text-primary shrink-0" />
                  <div className="flex flex-col">
                    <span className="text-[11px] text-muted-foreground">
                      Duration & Arrival Window
                    </span>
                    <span className="text-xs font-medium text-foreground">
                      {Math.round(job.estimatedDurationSeconds / 60)} mins
                      {job.windowStartOffsetSeconds != null && job.windowEndOffsetSeconds != null
                        ? ` (${formatOffsetTime(job.windowStartOffsetSeconds)} – ${formatOffsetTime(
                            job.windowEndOffsetSeconds
                          )})`
                        : ' (Flexible window)'}
                    </span>
                  </div>
                </div>
              </div>

              {job.timingMode === 'range' && (
                <div className="p-2.5 rounded-xl border border-amber-500/30 bg-amber-500/10 flex items-center justify-between text-xs text-amber-800 dark:text-amber-300">
                  <div className="flex items-center gap-1.5">
                    <CalendarDays className="h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-400" />
                    <span>
                      Multi-Day SLA Range: {job.earliestServiceDate || job.serviceDate} →{' '}
                      {job.deadlineServiceDate || job.serviceDate}
                    </span>
                  </div>
                  <Badge
                    variant="outline"
                    className="text-[10px] bg-amber-500/20 border-amber-500/40"
                  >
                    Flexible Range
                  </Badge>
                </div>
              )}

              {job.timingMode === 'multi_slot' && (
                <div className="p-2.5 rounded-xl border border-indigo-500/30 bg-indigo-500/10 flex items-center justify-between text-xs text-indigo-800 dark:text-indigo-300">
                  <div className="flex items-center gap-1.5">
                    <Layers className="h-3.5 w-3.5 shrink-0 text-indigo-600 dark:text-indigo-400" />
                    <span>
                      Customer Candidate Windows: {job.availabilityWindows?.length || 0} options
                    </span>
                  </div>
                  <Badge
                    variant="outline"
                    className="text-[10px] bg-indigo-500/20 border-indigo-500/40"
                  >
                    Disjunctive Slots
                  </Badge>
                </div>
              )}

              {/* Slot Finder Button */}
              <Button
                variant="outline"
                size="sm"
                className="w-full h-8 text-xs gap-1.5 text-primary border-primary/30 bg-primary/5 hover:bg-primary/10 transition-all font-semibold"
                onClick={() => setIsSlotFinderOpen(true)}
              >
                <Sparkles className="h-3.5 w-3.5 text-primary" />
                <span>Find Best Slot / Check Capacity</span>
              </Button>

              {/* Required Skills */}
              <div className="space-y-1.5 pt-2 border-t border-border/40">
                <div className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
                  <Wrench className="h-3.5 w-3.5 text-primary" />
                  <span>Required Skills</span>
                </div>
                {(job.requiredSkills || []).length === 0 ? (
                  <p className="text-xs text-muted-foreground italic">
                    No specific skill requirements. Any active operator can service this job.
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {(job.requiredSkills || []).map((skill) => (
                      <Badge
                        key={skill.id}
                        variant="secondary"
                        className="text-xs px-2 py-0.5"
                        style={
                          skill.color
                            ? {
                                backgroundColor: `${skill.color}20`,
                                color: skill.color,
                                borderColor: `${skill.color}40`,
                              }
                            : undefined
                        }
                      >
                        {skill.name}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Job Checklist / Tasks Card */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ListTodo className="h-4 w-4 text-primary" />
                  <span>Operator Checklist</span>
                </div>
                <Badge variant="outline" className="text-xs">
                  {filteredTasks.filter((t) => isTerminalStatus(taskStatuses, t.status)).length}/
                  {filteredTasks.length} Completed
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <form onSubmit={handleAddTask} className="flex gap-2">
                <Input
                  placeholder="Add item (e.g. Inspect filter, Take photo of meter)"
                  value={newTaskLabel}
                  onChange={(e) => setNewTaskLabel(e.target.value)}
                  className="h-8 text-xs flex-1"
                />
                <Button
                  type="submit"
                  size="sm"
                  className="h-8 text-xs shrink-0"
                  disabled={createTaskMutation.isPending || !newTaskLabel.trim()}
                >
                  {createTaskMutation.isPending ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Plus className="h-3 w-3 mr-1" />
                  )}
                  <span>Add Item</span>
                </Button>
              </form>

              <div className="space-y-2">
                {filteredTasks.length === 0 ? (
                  <p className="text-xs text-muted-foreground italic py-2">
                    No checklist items configured. Operators will mark the overall job complete upon
                    finish.
                  </p>
                ) : (
                  filteredTasks.map((t) => {
                    const done = isTerminalStatus(taskStatuses, t.status);
                    return (
                      <div
                        key={t.id}
                        className="flex items-center gap-2.5 p-2.5 rounded-lg border border-border/40 bg-card text-xs"
                      >
                        <CheckCircle2
                          className={`h-4 w-4 shrink-0 ${
                            done ? 'text-emerald-600' : 'text-muted-foreground/50'
                          }`}
                        />
                        <span
                          className={`flex-1 ${
                            done
                              ? 'line-through text-muted-foreground'
                              : 'text-foreground font-medium'
                          }`}
                        >
                          {t.label}
                        </span>
                      </div>
                    );
                  })
                )}
              </div>
            </CardContent>
          </Card>

          {/* Proof of Work & Photo Attachments */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Camera className="h-4 w-4 text-primary" />
                  <span>Proof of Work & Photo Gallery</span>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <JobAttachmentsSection jobId={job.id} />
            </CardContent>
          </Card>
        </div>

        {/* Right Column (1 Col): Assignment & Operator Actions & Internal Discussion */}
        <div className="space-y-6">
          {/* Customer & Contact (CRM) Card */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-primary" />
                  <span>Customer & Contact</span>
                </div>
                {job.contact && (
                  <Button
                    variant="ghost"
                    size="sm"
                    asChild
                    className="text-xs h-7 gap-1 text-muted-foreground hover:text-foreground"
                  >
                    <Link to={`/org/${orgId}/workspace/${workspaceId}/contacts/${job.contact.id}`}>
                      <span>CRM Profile</span>
                      <ExternalLink className="h-3 w-3" />
                    </Link>
                  </Button>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {job.contact ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm font-semibold text-foreground block">
                        {job.contact.name || 'Unnamed Customer'}
                      </span>
                      {job.contact.company && (
                        <span className="text-xs text-muted-foreground">{job.contact.company}</span>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2 pt-1">
                    {job.contact.phone && (
                      <a
                        href={`tel:${job.contact.phone}`}
                        className="flex items-center gap-2 text-xs text-primary hover:underline"
                      >
                        <Phone className="h-3.5 w-3.5" />
                        <span>{job.contact.phone}</span>
                      </a>
                    )}
                    {job.contact.email && (
                      <a
                        href={`mailto:${job.contact.email}`}
                        className="flex items-center gap-2 text-xs text-primary hover:underline"
                      >
                        <Mail className="h-3.5 w-3.5" />
                        <span>{job.contact.email}</span>
                      </a>
                    )}
                    {job.contact.address && (
                      <div className="flex items-start gap-2 text-xs text-muted-foreground">
                        <MapPin className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                        <span>{job.contact.address}</span>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="space-y-2 text-center py-3">
                  <p className="text-xs text-muted-foreground">
                    No customer record linked to this job.
                  </p>
                  <Button variant="outline" size="sm" asChild className="text-xs h-7 gap-1">
                    <Link to={`${basePath}/${job.id}/edit`}>
                      <Plus className="h-3 w-3" />
                      <span>Link Customer</span>
                    </Link>
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Operator Assignment Card */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <UserCheck className="h-4 w-4 text-primary" />
                  <span>Operator Assignment</span>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {job.assignedOperator ? (
                <div className="space-y-3">
                  <div className="p-3 rounded-lg border border-primary/20 bg-primary/5 flex items-center justify-between">
                    <div>
                      <span className="text-xs font-semibold text-foreground block">
                        {job.assignedOperator.operatorName}
                      </span>
                      <span className="text-[11px] text-muted-foreground">
                        Stop #
                        {job.assignedOperator.position != null
                          ? job.assignedOperator.position + 1
                          : 1}{' '}
                        on route
                      </span>
                    </div>
                    <Badge variant="secondary" className="text-xs">
                      Assigned
                    </Badge>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 text-xs text-destructive hover:bg-destructive/10 border-destructive/30 gap-1"
                      onClick={() => handleStatusTransition('unassigned')}
                      disabled={transitionMutation.isPending}
                    >
                      <UserX className="h-3.5 w-3.5" />
                      <span>Unassign from Route</span>
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="p-3 rounded-lg border border-border/50 bg-muted/20 text-xs text-muted-foreground">
                    This job is currently in the{' '}
                    <span className="font-semibold text-foreground">Unassigned Pool</span>.
                  </div>

                  <div className="space-y-2">
                    <span className="text-xs font-medium text-foreground">Assign to Operator</span>
                    <div className="flex gap-2">
                      <select
                        className="flex-1 h-8 rounded-md border border-input bg-background px-2 text-xs text-foreground focus:outline-hidden focus:ring-1 focus:ring-ring"
                        value={selectedOpToAssign}
                        onChange={(e) => setSelectedOpToAssign(e.target.value)}
                      >
                        <option value="">Select an operator...</option>
                        {operators.map((op) => (
                          <option key={op.id} value={op.id}>
                            {op.user?.fullName || op.user?.email || `Operator #${op.id}`}
                          </option>
                        ))}
                      </select>
                      <Button
                        size="sm"
                        className="h-8 text-xs shrink-0"
                        onClick={handleAssignOperator}
                        disabled={!selectedOpToAssign || assignJobMutation.isPending}
                      >
                        Assign
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Reschedule Date Section */}
              <div className="pt-3 border-t border-border/40 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-foreground">Service Date</span>
                  {!isRescheduling ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 text-[11px] text-primary px-2"
                      onClick={() => {
                        setNewDateValue(job.serviceDate);
                        setIsRescheduling(true);
                      }}
                    >
                      <CalendarDays className="h-3 w-3 mr-1" />
                      <span>Reschedule</span>
                    </Button>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 text-[11px] text-muted-foreground px-2"
                      onClick={() => setIsRescheduling(false)}
                    >
                      Cancel
                    </Button>
                  )}
                </div>

                {isRescheduling ? (
                  <div className="flex gap-2 pt-1">
                    <Input
                      type="date"
                      value={newDateValue}
                      onChange={(e) => setNewDateValue(e.target.value)}
                      className="h-8 text-xs flex-1"
                    />
                    <Button
                      size="sm"
                      className="h-8 text-xs"
                      onClick={handleReschedule}
                      disabled={!newDateValue || rescheduleMutation.isPending}
                    >
                      Save Date
                    </Button>
                  </div>
                ) : (
                  <span className="text-xs text-muted-foreground block">{job.serviceDate}</span>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Status & Lifecycle Actions */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Truck className="h-4 w-4 text-primary" />
                <span>Status Transitions</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-col gap-1 text-xs">
                <span className="text-muted-foreground">Current Status</span>
                <span className="font-semibold text-foreground capitalize">
                  {job.status.replace('_', ' ')}
                </span>
              </div>

              <div className="flex flex-col gap-2 pt-2 border-t border-border/40">
                <span className="text-xs font-semibold text-foreground">Manual Override</span>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs justify-start"
                    disabled={job.status === 'unassigned' || transitionMutation.isPending}
                    onClick={() => handleStatusTransition('unassigned')}
                  >
                    Unassigned
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs justify-start"
                    disabled={job.status === 'in_progress' || transitionMutation.isPending}
                    onClick={() => handleStatusTransition('in_progress')}
                  >
                    In Progress
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs justify-start text-emerald-600"
                    disabled={job.status === 'completed' || transitionMutation.isPending}
                    onClick={() => handleStatusTransition('completed')}
                  >
                    Completed
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs justify-start text-rose-600"
                    disabled={job.status === 'canceled' || transitionMutation.isPending}
                    onClick={() => handleStatusTransition('canceled')}
                  >
                    Canceled
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Internal Discussion */}
          <Card className="flex flex-col min-h-[400px]">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <MessageSquare className="h-4 w-4 text-primary" />
                <span>Team Discussion</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="flex-1 p-0 flex flex-col">
              <div className="flex-1 min-h-[320px] p-3">
                <EntityCommentThread target={{ type: 'job', jobId: job.id }} />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <JobSlotFinderDialog
        job={job}
        isOpen={isSlotFinderOpen}
        onClose={() => setIsSlotFinderOpen(false)}
        operators={operators}
        onSlotSelected={() => {
          utils.jobPlan.jobs.getById.invalidate({ id: numJobId });
          utils.jobPlan.getDaySchedule.invalidate();
        }}
      />
    </div>
  );
}
